/**
 * Careline's tool handlers: what the host calls over the direct bridge (`bridge.ts` registers them). Each
 * tool's schema, title and policy live in `contract.ts`; a handler parses its arguments and its output
 * with those same schemas, runs the same store actions the clinician's own controls use, and answers with
 * the structured result plus one line a person can read.
 *
 * A review tool (`update_note`, `set_review_status`, `review_result`) answers one of two ways (own-app views
 * FR-7), chosen by what the host said it does in its welcome (`host.views`), never by platform:
 * - a host that renders Careline's `ui://` views gets the review payload (`review.ts`) and NOTHING is
 *   written; the view makes the app-only write (`apply_note_draft` / `record_review`) once the clinician
 *   decides;
 * - any other host gets the in-app card: Careline waits for the clinician, then writes. The handler's
 *   `signal` aborts when the host stops waiting; the card must close then, and a Save or Confirm that lands
 *   after it writes nothing.
 */

import type { McpResult, ToolContext, ToolHandler } from "@talkie/agentic-app-transport";
import { cancelledByThePerson } from "../../shared/outcome.mjs";
import { outputs, tools } from "./contract";
import {
  AppError,
  patients,
  getPatient,
  patientMatches,
  resultsFor,
  medicationsFor,
  historyFor,
  catalogs,
  pharmacies,
  type Store,
  type Scope,
  type Note,
} from "./domain.js";
import { buildNoteReview, buildReviewConfirm, type ReviewItem } from "./review.js";

export type ViewRequest = { patient_id: string; section: string; order_id?: string };
/** A note draft waiting on the clinician: `note` is exactly what Dobby proposed. */
export type NoteReviewRequest = { patient_id: string; encounter_id: string; note: Partial<Note> };
export type { ReviewItem };
export type ReviewConfirmRequest = { patient_id: string; encounter_id: string; item: ReviewItem };
export type ViewPort = {
  show: (request: ViewRequest) => void;
  assertAvailable: () => void;
  /**
   * Open `request` for the clinician and settle with the sections they SAVED (edited or not). Rejects with
   * AppError `cancelled` when they discard it, and must close the card when `signal` aborts (the host
   * stopped waiting), so a late Save cannot write after Dobby was told nothing was written.
   */
  reviewNote: (request: NoteReviewRequest, signal: AbortSignal) => Promise<Partial<Note>>;
  /**
   * Ask the clinician to confirm `request` and settle when they Confirm. Rejects with AppError `cancelled` when they
   * choose Not yet (or leave), and must close the card when `signal` aborts, so a late Confirm cannot record.
   */
  confirmReview: (request: ReviewConfirmRequest, signal: AbortSignal) => Promise<void>;
};

const patientOf = (p: ReturnType<typeof getPatient>) => ({ patient_id: p.id, name: p.name, dob: p.dob });
const encounterOf = (v: { id: string; revision: number; signed: boolean }) => ({ id: v.id, revision: v.revision, signed: v.signed });
function chartFactsFor(p: ReturnType<typeof getPatient>) {
  return {
    patient_id: p.id,
    name: p.name,
    age: p.age,
    sex: p.sex,
    dob: p.dob,
    allergy: p.allergy,
    reaction: p.reaction,
    problems: p.problems,
    vitals: { bp: p.bp, pulse: p.pulse, weight: p.weight, bmi: p.bmi },
    social: p.social,
    family: p.family,
    surgery: p.surgery,
  };
}
/** One line a person can read; the structured result carries the detail. Never echoes note text. */
function summarize(name: string, data: Record<string, any>): string {
  // A review payload carries clinical text: the line names what is waiting, never the content.
  if (data.kind === "note_review") return `${name}: a note draft is ready for the clinician to review · encounter revision ${data.encounter.revision} · nothing was written.`;
  if (data.kind === "review_confirm") return `${name}: a review is ready for the clinician to confirm · encounter revision ${data.encounter.revision} · nothing was recorded.`;
  if (outputs.mutation.safeParse(data).success)
    return `${name}: ${data.status}${data.entity_id ? ` (${data.entity_id})` : ""} · patient ${data.patient_id} · encounter revision ${data.revision} · ${data.persistence === "local" ? "saved in this browser" : "session-only"} · simulated.`;
  switch (name) {
    case "list_schedule":
    case "search_patients":
      return `${data.patients.length} fictional patient(s): ${data.patients.map((p: any) => `${p.name} (${p.patient_id})`).join(", ") || "none"}.`;
    case "get_chart_summary":
      return `Chart for ${data.patient.name} (${data.patient.patient_id}); ${data.medications.length} active medication(s); encounter revision ${data.encounter.revision}${data.encounter.signed ? ", signed" : ""}.`;
    case "get_visit_history":
      return `${data.visits.length} visit(s).`;
    case "get_medications":
      return `${data.active.length} active medication(s); ${data.prescriptions.length} prescription order(s) this visit.`;
    case "get_results":
      return `${data.results.length} final result(s); ${data.awaiting_results.length} order(s) awaiting results.`;
    case "get_result":
      return `${data.result.name}: ${data.result.value} ${data.result.unit} (${data.result.flag}).`;
    case "get_encounter":
      return `Encounter ${data.encounter.id} at revision ${data.encounter.revision}${data.encounter.signed ? " (signed)" : ""}.`;
    case "list_orders":
      return `${data.orders.length} order(s); encounter revision ${data.revision}.`;
    case "list_orderables":
      return `Orderable categories: ${Object.keys(data.catalogs).join(", ")}; prescriptions are clinician-entered.`;
    case "get_workflow_status":
      return data.signed
        ? "Encounter signed."
        : data.blockers.length
          ? `Blocked: ${data.blockers.join(" ")}`
          : `Ready to sign at revision ${data.revision}.`;
    case "get_after_visit_summary":
      return `After-visit summary (${data.final ? "final" : "not final until signed"}); ${data.orders.length} order(s).`;
    case "show_chart":
      return `Showing ${data.section} for patient ${data.patient_id}.`;
    case "review_order":
      return `Review ${data.order.type} ${data.order.name} (${data.order.status}); sign with expected_revision ${data.revision}.`;
    default:
      return `${name} completed.`;
  }
}

type ToolSpec = (typeof tools)[number];

/**
 * A handler per manifest tool, over `store` and the page's `view`. `rendersViews` answers whether the
 * connected host renders Careline's own views (its welcome's `host.views`); it is read per call, so a
 * reconnect to a different host takes effect without re-registering.
 */
export function ehrHandlers(store: Store, view: ViewPort, rendersViews: () => boolean): Record<string, ToolHandler> {
  return Object.fromEntries(
    tools.map((spec) => [
      spec.name,
      (args: unknown, { signal }: ToolContext) => callTool(store, view, rendersViews, spec, args, signal),
    ]),
  );
}

async function callTool(
  store: Store,
  view: ViewPort,
  rendersViews: () => boolean,
  spec: ToolSpec,
  args: unknown,
  signal: AbortSignal,
): Promise<McpResult> {
  try {
    const parsed = spec.schema.safeParse(args ?? {});
    if (!parsed.success) throw new AppError("invalid_input", "Arguments do not match this tool schema.");
    const a = parsed.data as Record<string, any>;
    let data: Record<string, unknown>;
    if (spec.write) {
      view.assertAvailable();
      const { patient_id, encounter_id, expected_revision, request_id, ...payload } = a;
      const item: ReviewItem =
        spec.name === "review_result"
          ? { kind: "results", result_ids: payload.result_ids }
          : { kind: "chart", field: payload.field, reviewed: payload.reviewed };
      if (spec.review && rendersViews()) {
        // The host renders the view: hand it exactly what the clinician decides on. Nothing is written here.
        const scope = { patient_id, encounter_id, expected_revision };
        data =
          spec.name === "update_note"
            ? buildNoteReview(store, scope, payload.note)
            : buildReviewConfirm(store, scope, item);
      } else {
        if (spec.review) {
          if (spec.name === "update_note")
            payload.note = await view.reviewNote({ patient_id, encounter_id, note: payload.note }, signal);
          else await view.confirmReview({ patient_id, encounter_id, item }, signal);
          // The host may stop waiting the instant before a Save/Confirm lands; Dobby was then told nothing was written.
          if (signal.aborted)
            throw new AppError("expired", "Dobby stopped waiting for the clinician; nothing was written.");
        }
        data = store.run(
          spec.name,
          { patient_id, encounter_id, expected_revision, request_id } as Scope,
          payload,
          "agent",
        );
      }
    } else {
      const p = a.patient_id ? getPatient(a.patient_id) : undefined;
      const v = p ? store.encounter(p.id, a.encounter_id) : undefined;
      switch (spec.name) {
        case "list_schedule":
        case "search_patients":
          data = {
            patients: patients
              .filter((p) => spec.name === "list_schedule" || patientMatches(p, a.query))
              .map((p) => ({
                patient_id: p.id,
                encounter_id: store.encounter(p.id).id,
                name: p.name,
                time: p.time,
                reason: p.reason,
                status: store.encounter(p.id).signed ? "Complete" : p.status,
              })),
          };
          break;
        case "get_chart_summary":
          data = {
            patient: chartFactsFor(p!),
            latest_visit: historyFor(p!.id, v!)[0],
            medications: medicationsFor(p!.id, v!),
            encounter: encounterOf(v!),
          };
          break;
        case "get_visit_history":
          data = { visits: historyFor(p!.id, v!) };
          break;
        case "get_medications":
          data = {
            patient: patientOf(p!),
            encounter: encounterOf(v!),
            active: medicationsFor(p!.id, v!),
            prescriptions: v!.orders.filter((o) => o.type === "Prescription"),
            historical: p!.meds.map(([name, strength, directions, date]) => ({
              name,
              strength,
              directions,
              date,
            })),
          };
          break;
        case "get_results":
          data = {
            patient: patientOf(p!),
            encounter: encounterOf(v!),
            results: resultsFor(p!.id).map((r, i) => ({ ...r, reviewed: v!.resultReviewed.includes(i) })),
            awaiting_results: v!.orders.filter(
              (o) =>
                ["Laboratory", "Imaging", "Diagnostic test"].includes(o.type) &&
                o.status === "Ordered (simulated)",
            ),
          };
          break;
        case "get_result": {
          const r = resultsFor(p!.id).find((r) => r.id === a.result_id);
          if (!r) throw new AppError("not_found", "Result not found for this patient.");
          data = { result: r };
          break;
        }
        case "get_encounter":
          data = { encounter: v! };
          break;
        case "list_orders":
          data = { orders: v!.orders, revision: v!.revision };
          break;
        case "list_orderables":
          data = { catalogs, prescription: { entry: "clinician supplied", pharmacies: [...pharmacies] } };
          break;
        case "get_workflow_status":
          data = {
            encounter_id: v!.id,
            revision: v!.revision,
            signed: v!.signed,
            blockers: store.blockers(p!.id),
          };
          break;
        case "get_after_visit_summary":
          data = {
            final: v!.signed,
            instructions: v!.note.instructions,
            followup: v!.note.followup,
            diagnoses: v!.diagnoses,
            orders: v!.orders,
          };
          break;
        case "show_chart":
          view.assertAvailable();
          view.show({ patient_id: p!.id, section: a.section });
          data = { patient_id: p!.id, section: a.section };
          break;
        case "review_order": {
          const o = store.order(v!, a.order_id);
          // A renderer host shows the order in its own view (FR-6); only a non-renderer host's canvas navigates.
          if (!rendersViews()) {
            view.assertAvailable();
            view.show({ patient_id: p!.id, section: "order-review", order_id: o.id });
          }
          data = { patient: patientOf(p!), patient_id: p!.id, encounter_id: v!.id, revision: v!.revision, order: o };
          break;
        }
        default:
          throw new AppError("invalid_input", "Unsupported tool.");
      }
    }
    const structuredContent = spec.output.parse({
      simulated: true,
      persistence: store.status().persistence,
      data,
    }) as { data: Record<string, any> };
    return {
      structuredContent,
      content: [{ type: "text" as const, text: summarize(spec.name, structuredContent.data) }],
    };
  } catch (error) {
    if (error instanceof AppError && error.code === "cancelled")
      return cancelledByThePerson(`${error.message} (Simulated demo; nothing was changed.)`);
    return {
      isError: true,
      content: [
        {
          type: "text" as const,
          // `<stable_code>: <message>` — the code is the machine-readable part (FR-14);
          // no structuredContent, which is the success shape the host validates.
          text: `${error instanceof AppError ? error.code : "invalid_input"}: ${
            error instanceof AppError ? error.message : "The requested EHR operation failed validation."
          } (Simulated demo; nothing was changed.)`,
        },
      ],
    };
  }
}
